import { useSortable } from '@dnd-kit/sortable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/sharp-solid-svg-icons';
import { Button } from 'eone-ui/buttons/button';
import { ColorPicker } from 'eone-ui/color-picker/color-picker';
import { DatePicker } from 'eone-ui/datepicker/datepicker';
import { getDataTestId } from 'eone-ui/functions/data-test-ids';
import { InputNumber } from 'eone-ui/inputs/input-number';
import { InputString } from 'eone-ui/inputs/input-string';
import { InputTime } from 'eone-ui/inputs/input-time';
import { MultiValueTypeInput } from 'eone-ui/multi-value-type-input/multi-value-type-input';
import { Select } from 'eone-ui/select/select';
import { TableAlignment, TableFieldType } from 'eone-ui/tables/table-constants';
import React, { useMemo, useState } from 'react';

function TableRowSpacer() {
  return <td className="eone-ui-table-row-spacer" />;
}

function TableRowButtonEmptyPlaceholder(props) {
  return <div className={props.Class} style={{ visibility: 'hidden' }} />;
}

function TableRowButton(props) {
  const [isHovered, setIsHovered] = useState(false);

  const setHover = () => {
    setIsHovered(true);
  };

  const clearHover = () => {
    setIsHovered(false);
  };

  const onClick = () => {
    props.OnClick();
  };

  const className = props.Cancel ? 'eone-ui-row-button-danger' : 'eone-ui-table-row-button';
  const icon = isHovered && props.HoverIcon ? props.HoverIcon : props.Icon;

  if (props.Hidden) {
    return <TableRowButtonEmptyPlaceholder Class={className} />;
  }

  return (
    <div onMouseOver={setHover} onMouseOut={clearHover}>
      <Button
        Class={className}
        Icon={icon}
        OnClick={onClick}
        data-testid={props.DataTestId}
        Title={props.Title}
      />
    </div>
  );
}

function TableRowButtons(props) {
  const getButtons = () => {
    if (!props.Buttons || props.Buttons.length === 0) {
      return null;
    }

    return props.Buttons.map((button, index) => {
      const hidden = button.Hidden != undefined ? props.Row[button.Hidden] : false;

      return (
        <TableRowButton
          key={index}
          Icon={button.Icon}
          HoverIcon={button.HoverIcon}
          Title={button.Title}
          Cancel={button.Cancel}
          Hidden={hidden}
          DataTestId={getDataTestId(props.DataTestId, props.Row[props.IdField], button.Title)}
          OnClick={() =>
            props.IdField ? button.OnClick(props.Row[props.IdField]) : button.OnClick()
          }
          Label={button.Label}
        />
      );
    });
  };

  if (!props.Buttons || props.Buttons.length === 0) {
    return null;
  }

  return (
    <td className="eone-ui-table-row-button-group">
      <div className="eone-ui-table-row-button-group-container">{getButtons()}</div>
    </td>
  );
}

function TableRowItem(props) {
  const changeValueType = (valueType) => {
    if (typeof props.ChangeValue === 'function') {
      props.ChangeValue(props.FieldValueType, valueType);
    }
  };

  const changeValue = (value) => {
    if (typeof props.ChangeValue === 'function') {
      props.ChangeValue(props.FieldName, value);
    }
  };

  const updateValue = (value) => {
    if (typeof props.UpdateValue === 'function') {
      props.UpdateValue(props.FieldName, value);
    }
  };

  const changeAndUpdateValue = (value) => {
    changeValue(value);
    updateValue(value);
  };

  const toggleCheckboxValue = () => {
    const oldValue = props.Row[props.FieldName];
    const newValue = !oldValue;
    changeValue(newValue);
    updateValue(newValue);
  };

  const getOptions = () => {
    if (props.Options) {
      if (Array.isArray(props.Options)) {
        return props.Options;
      }
      if (props.Options in props.Row) {
        return props.Row[props.Options];
      }
    }
    return [];
  };

  const getOptionValue = (options, key) => {
    for (let i = 0, len = options.length; i < len; i++) {
      if (options[i].Key === key) {
        return options[i].Value;
      }
    }
    return key;
  };

  const getOptionIcon = (key) => {
    const options = getOptions();
    for (let i = 0, len = options.length; i < len; i++) {
      if (options[i].Key === key) {
        return options[i].Icon;
      }
    }
    return null;
  };

  const getSelectLabelValue = (value) => {
    const options = getOptions();
    return getOptionValue(options, value);
  };

  const getDateLabelValue = (value, userTimeZone) => {
    if (!value) return '';
    const dateValue = new Date(value);
    const utcDateValue = new Date(
      Date.UTC(
        dateValue.getFullYear(),
        dateValue.getMonth(),
        dateValue.getDate(),
        dateValue.getHours(),
        dateValue.getMinutes(),
        dateValue.getSeconds(),
      ),
    );
    const options = { timeZone: userTimeZone || 'UTC' };
    return utcDateValue.toLocaleDateString(props.Locale, options);
  };

  const getTimeLabelValue = (value, userTimeZone) => {
    if (!value) return '';
    const dateValue = new Date(value);
    const utcDateValue = new Date(
      Date.UTC(
        dateValue.getFullYear(),
        dateValue.getMonth(),
        dateValue.getDate(),
        dateValue.getHours(),
        dateValue.getMinutes(),
        dateValue.getSeconds(),
      ),
    );
    const options = { timeZone: userTimeZone || 'UTC' };
    return utcDateValue.toLocaleTimeString(props.Locale, options);
  };

  const getPercentageLabelValue = (value) => {
    if (!value)
      return Number(0).toLocaleString(props.Locale, { style: 'percent', minimumFractionDigits: 2 });
    return Number(value).toLocaleString(props.Locale, {
      style: 'percent',
      minimumFractionDigits: 2,
    });
  };

  const getDateTimeLabelValue = (value, userTimeZone) => {
    if (!value) return null;
    const dateValue = new Date(value);
    const utcDateValue = new Date(
      Date.UTC(
        dateValue.getFullYear(),
        dateValue.getMonth(),
        dateValue.getDate(),
        dateValue.getHours(),
        dateValue.getMinutes(),
        dateValue.getSeconds(),
      ),
    );
    const options = { timeZone: userTimeZone || 'UTC' };
    const datePart = utcDateValue.toLocaleDateString(props.Locale, options);
    const timePart = utcDateValue.toLocaleTimeString(props.Locale, options);
    return datePart + ' ' + timePart;
  };

  const getLabelValue = (value, isArray, userTimeZone) => {
    if (isArray) {
      if (!value) return '';
      const labels: string[] = [];
      for (let i = 0, len = value.length; i < len; i++) {
        labels.push(getLabelValue(value[i], false, userTimeZone));
      }
      labels.sort();
      return labels.join(', ');
    }
    switch (props.FieldType) {
      case TableFieldType.Checkbox:
        return value ? 'Yes' : 'No';
      case TableFieldType.Select:
        return getSelectLabelValue(value);
      case TableFieldType.Date:
        return getDateLabelValue(value, userTimeZone);
      case TableFieldType.Time:
        return getTimeLabelValue(value, userTimeZone);
      case TableFieldType.DateTime:
        return getDateTimeLabelValue(value, userTimeZone);
      case TableFieldType.Percent:
        return getPercentageLabelValue(value);
      default:
        return value;
    }
  };

  const getClass = () => {
    let className = 'eone-ui-table-row-data';
    switch (props.Alignment) {
      case TableAlignment.Center:
        className = `${className} eone-ui-table-row-data-center`;
        break;
      case TableAlignment.Right:
        className = `${className} eone-ui-table-row-data-right`;
        break;
    }
    return className;
  };

  const getFieldType = () => {
    if (props.FieldType) {
      if (Number.isInteger(props.FieldType)) {
        return props.FieldType;
      }
      if (props.FieldType in props.Row) {
        return props.Row[props.FieldType];
      }
    }
    return TableFieldType.String;
  };

  const getValueTypes = () => {
    return props.ValueTypes || [];
  };

  const value = props.Row[props.FieldName];
  const fieldType = getFieldType();
  const label = getLabelValue(value, props.Array, props.UserTimeZone);

  const getCell = () => {
    if (fieldType === TableFieldType.MultiValue) {
      const valueTypes = getValueTypes();
      const valueType = props.Row[props.FieldValueType];
      return (
        <td className="eone-ui-table-row-data" style={{ width: props.Width }}>
          <MultiValueTypeInput
            ReadOnly={!props.Editable}
            ValueTypes={valueTypes}
            ValueType={valueType}
            Value={value}
            ContainerClass="eone-ui-input-table"
            ValidChars={props.ValidChars}
            InvalidChars={props.InvalidChars}
            Validate={props.Validate}
            Placeholder={props.Placeholder}
            ChangeValueType={changeValueType}
            ChangeValue={changeValue}
            UpdateValue={updateValue}
          />
        </td>
      );
    }

    if (fieldType === TableFieldType.Color) {
      return (
        <td className="eone-ui-table-row-data">
          <ColorPicker ReadOnly={!props.Editable} Value={value} OnChange={changeAndUpdateValue} />
        </td>
      );
    }

    if (props.Editable) {
      switch (fieldType) {
        case TableFieldType.Checkbox:
          return <input type="checkbox" onChange={toggleCheckboxValue} checked={value} />;
        case TableFieldType.Select:
          return (
            <Select
              Placeholder={props.Placeholder}
              MultiSelect={props.Array}
              Options={getOptions()}
              Value={value}
              ContainerClass="eone-ui-input-table-select"
              Search={props.ShowSearch}
              Loading={props.Loading}
              OnChange={changeAndUpdateValue}
            />
          );
        case TableFieldType.String:
          return (
            <InputString
              Value={value}
              ContainerClass="eone-ui-input-table"
              ValidChars={props.ValidChars}
              Placeholder={props.Placeholder}
              InvalidChars={props.InvalidChars}
              Validate={props.Validate}
              OnChange={changeValue}
              OnBlur={updateValue}
            />
          );
        case TableFieldType.Integer:
          return (
            <InputNumber
              Value={value}
              ContainerClass="eone-ui-input-table"
              Locale={props.Locale}
              Placeholder={props.Placeholder}
              MaximumValue={props.MaximumValue}
              MinimumValue={props.MinimumValue}
              AllowNegative={props.AllowNegative}
              Validate={props.Validate}
              OnChange={changeValue}
              OnBlur={updateValue}
            />
          );
        case TableFieldType.Date:
          return (
            <DatePicker
              Value={value}
              ContainerClass="eone-ui-input-table-date"
              FixedMenu
              Locale={props.Locale}
              OnChange={changeAndUpdateValue}
            />
          );
        case TableFieldType.Time:
          return (
            <InputTime
              Value={value}
              ContainerClass="eone-ui-input-table-date"
              FixedMenu
              Locale={props.Locale}
              OnChange={changeAndUpdateValue}
            />
          );
      }
    }

    if (fieldType === TableFieldType.Select) {
      const icon = getOptionIcon(value);
      if (icon) {
        return (
          <div>
            <span className="eone-ui-select-icon">
              <FontAwesomeIcon icon={icon} />
            </span>
            <span>{label}</span>
          </div>
        );
      }
    }
    return label;
  };

  const expandableHandler = useMemo(() => {
    return (
      <React.Fragment>
        {props.Index == 0 && props.ShowExpand && props.IsExpanded == true && (
          <FontAwesomeIcon
            onClick={() => props.HandleExpand(props.Row[props.IdField])}
            style={{ paddingRight: 10 }}
            icon={faChevronDown}
          />
        )}
        {props.Index == 0 && props.ShowExpand && props.IsExpanded == false && (
          <FontAwesomeIcon
            onClick={() => props.HandleExpand(props.Row[props.IdField])}
            style={{ paddingRight: 10 }}
            icon={faChevronRight}
          />
        )}
      </React.Fragment>
    );
  }, [props]);

  if (props.Visible === false) {
    return <td className="eone-ui-table-row-data" />;
  }

  return (
    <td
      title={label}
      className={getClass()}
      style={{
        width: props.Width,
        /*paddingLeft: props.Index == 0 ? props.Depth * props.ChildPadding : 1,*/
      }}
    >
      {expandableHandler}
      {getCell()}
    </td>
  );
}

function TableRow(props) {
  const { isDragging, transform } = useSortable({
    id: props.Row[props.IdField],
    transition: { duration: 25, easing: 'cubic-bezier(1, 1, 0.5, 1)' },
  });

  const changeValue = (fieldName, value) => {
    if (typeof props.ChangeValue === 'function') {
      props.ChangeValue(props.Row[props.IdField], fieldName, value);
    }
  };

  const updateValue = (fieldName, value) => {
    if (typeof props.UpdateValue === 'function') {
      props.UpdateValue(props.Row[props.IdField], fieldName, value);
    }
  };

  const selectRow = () => {
    if (typeof props.SelectRow === 'function') {
      props.SelectRow(props.Row[props.IdField]);
    }
  };

  const getClass = () => {
    if (transform) {
      if (isDragging) {
        return 'eone-ui-table-row eone-ui-table-row-draggable eone-ui-table-row-dragging';
      } else {
        return 'eone-ui-table-row eone-ui-table-row-draggable';
      }
    }

    if (props.Selected) {
      return 'eone-ui-table-row eone-ui-table-row-selected';
    }
    return 'eone-ui-table-row';
  };

  const isColumnSelected = (column, selectedColumns) => {
    if (
      selectedColumns &&
      selectedColumns.length > 0 &&
      !selectedColumns.includes(column.FieldName)
    ) {
      return false;
    }
    return column.Visible === undefined || column.Visible === true;
  };

  const getSelectedColumns = () => {
    const columns: any[] = [];
    for (let i = 0, len = props.Columns.length; i < len; i++) {
      if (isColumnSelected(props.Columns[i], props.SelectedColumns)) {
        columns.push(props.Columns[i]);
      }
    }
    return columns;
  };

  const getItems = () => {
    if (!props.Columns) return null;
    const columns = getSelectedColumns();
    const defaultWidth = `${100 / columns.length}%`;
    return columns.map((column, index) => {
      const width = column.Width ?? defaultWidth;
      const visible = column.CellVisible && props.Row[column.CellVisible];
      const fieldType = column.FieldType || props.Row[column.RowFieldType] || TableFieldType.String;
      const options = column.Options || props.Row[column.RowOptions] || [];
      const valueTypes = column.ValueTypes || props.Row[column.RowValueTypes] || [];
      const maximumValue = column.MaximumValue || props.Row[column.RowMaximumValue];
      const minimumValue = column.MinimumValue || props.Row[column.RowMinimumValue];
      return (
        <TableRowItem
          Index={index}
          ShowExpand={props.ShowExpand}
          HandleExpand={props.HandleExpand}
          IsExpanded={props.IsExpanded}
          Depth={props.Depth}
          IdField={props.IdField}
          ChildPadding={props.ChildPadding}
          key={index}
          Width={width}
          FieldName={column.FieldName}
          FieldValueType={column.FieldValueType}
          FieldType={fieldType}
          Placeholder={column.Placeholder}
          ValidChars={column.ValidChars}
          InvalidChars={column.InvalidChars}
          MaximumValue={maximumValue}
          MinimumValue={minimumValue}
          Validate={column.Validate}
          Array={column.Array}
          UserTimeZone={column.UserTimeZone}
          Alignment={column.Alignment}
          ValueTypes={valueTypes}
          Options={options}
          ShowSearch={column.ShowSearch}
          Loading={column.Loading}
          Editable={column.Editable}
          Row={props.Row}
          Visible={visible}
          Locale={props.Locale}
          ChangeValue={changeValue}
          UpdateValue={updateValue}
          DataTestId={props.DataTestId}
        />
      );
    });
  };

  if (props.Visible !== undefined && !props.Visible) {
    return null;
  }

  return (
    <tr className={getClass()} onClick={selectRow}>
      <TableRowSpacer />
      {getItems()}
      <TableRowButtons
        Buttons={props.Buttons}
        Row={props.Row}
        IdField={props.IdField}
        DataTestId={props.DataTestId}
        Width={props.ButtonWidth}
      />
      <TableRowSpacer />
    </tr>
  );
}

export { TableRow };
